import BaseWidgetController from './base_widget_controller';
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { GRAY_700 } from '../../../shared/colors';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip);

/**
 * Base controller for bar chart widgets on the dashboard.
 * Extends BaseWidgetController to add Chart.js bar chart rendering.
 *
 * Child controllers should:
 * 1. Define their own static values for labels (e.g., `labelOne`, `labelTwo`)
 * 2. Override `getChartConfig(data)` to return { labels, values, colors }
 * 3. Import colors from shared/colors.js to maintain consistency
 *
 * Example:
 *   import { PURPLE_400, GREEN_400 } from '../../../shared/colors';
 *
 *   getChartConfig(data) {
 *     return {
 *       labels: [this.labelOneValue, this.labelTwoValue],
 *       values: [data.total, data.completed],
 *       colors: [PURPLE_400, GREEN_400],
 *     };
 *   }
 */
export default class extends BaseWidgetController {
  static targets = [...BaseWidgetController.targets, 'chart'];

  connect() {
    super.connect();
    this.chart = null;
  }

  disconnect() {
    super.disconnect();
    this.destroyChart();
  }

  showLoading() {
    super.showLoading();

    if (this.hasChartTarget) {
      this.chartTarget.classList.add('opacity-50');
    }
  }

  hideLoading() {
    super.hideLoading();

    if (this.hasChartTarget) {
      this.chartTarget.classList.remove('opacity-50');
    }
  }

  renderData(data) {
    super.renderData(data);
    this.renderChart(data);
  }

  renderChart(data) {
    if (!this.hasChartTarget) return;

    this.destroyChart();

    const config = this.getChartConfig(data);
    if (!config) return;

    const { labels, values, colors } = config;
    const ctx = this.chartTarget.getContext('2d');
    const maxValue = Math.max(...values, 1);

    this.chart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels.map(() => ''),
        datasets: [
          {
            data: values,
            backgroundColor: colors,
            hoverBackgroundColor: colors,
            borderWidth: 0,
            borderRadius: 4,
            barThickness: 100,
          },
        ],
      },
      options: this.getChartOptions(maxValue),
    });
  }

  getChartOptions(maxValue) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context) => this.formatTooltipLabel(context),
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: GRAY_700,
            font: {
              size: 12,
            },
          },
        },
        y: {
          beginAtZero: true,
          suggestedMax: maxValue * 1.1,
          grid: {
            display: false,
          },
          ticks: {
            color: GRAY_700,
            font: {
              size: 12,
            },
            callback: (value) => this.formatNumber(value),
          },
        },
      },
    };
  }

  /**
   * Override in child controllers to customize tooltip display.
   * Default: formatted number
   */
  formatTooltipLabel(context) {
    return this.formatNumber(context.raw);
  }

  /**
   * Override in child controllers to provide chart data.
   * Must return: { labels: string[], values: number[], colors: string[] }
   */
  getChartConfig(_data) {
    throw new Error('Child controllers must implement getChartConfig(data)');
  }

  destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
